<template>
  <!-- The desktop width and AppShell's reserved margin must stay in sync. Smaller desktop padding
       keeps room for labels in the compact sidebar; mobile retains its existing width. -->
  <aside
    :class="[
      'fixed top-0 mt-16 h-[calc(100vh-4rem)] flex-col border-l border-gray-200 bg-white px-4 text-gray-900 transition-all duration-300 ease-in-out dark:border-gray-800 dark:bg-gray-900 lg:flex lg:mt-0 lg:h-screen lg:px-3',
      'right-0',
      {
        'flex': isMobileOpen,
        'hidden': !isMobileOpen,
        'lg:w-[250px]': isExpanded || isMobileOpen || isHovered,
        'lg:w-[90px]': !isExpanded && !isHovered,
        'translate-x-0 w-[290px] pb-16': isMobileOpen,
        'translate-x-full': !isMobileOpen,
        'lg:translate-x-0': true,
      },
    ]"
  >
    <!-- Keep the menu independently scrollable; reveal its thin scrollbar for pointer and keyboard
         interaction without changing the available width or chaining wheel gestures to the page. -->
    <div ref="scrollElement" class="sidebar-scroll flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain pb-6 pt-3" @scroll.passive="scheduleSave">
      <nav v-if="compact" aria-label="منوی اصلی پنل، حالت فشرده" class="flex flex-col items-center gap-1">
        <template v-for="group in menuGroups" :key="group.id">
          <template v-if="!group.title">
            <router-link v-for="item in group.items" :key="item.id" :to="item.path" :title="item.name" :aria-label="item.name" :aria-current="isMenuCurrent(group, item.path) ? 'page' : undefined" class="flex h-11 w-12 items-center justify-center rounded-xl" :class="isMenuCurrent(group, item.path) ? 'bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-200' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'" @click="selectMenuPath(group, item.path)"><component :is="item.icon" class="h-5 w-5" /></router-link>
          </template>
          <button v-else type="button" :title="group.title" :aria-label="group.title" :aria-expanded="compactGroup?.id === group.id" aria-controls="compact-menu-panel" class="relative flex h-11 w-12 items-center justify-center rounded-xl" :class="group.items.some(item => item.subItems ? hasCanonicalActiveSubItem(item) : isCanonicalCurrent(item.path)) ? 'bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-200' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'" @click="openCompactGroup(group, $event)">
            <component :is="compactIcons[group.id] || group.items[0]?.icon || MoreHorizontal" class="h-5 w-5" />
            <span v-if="group.items.some(item => badgeCount(item.badge) > 0 || item.subItems?.some(sub => badgeCount(sub.badge) > 0))" class="absolute right-1 top-1 h-2 w-2 rounded-full bg-orange-700" aria-label="کار منتظر رسیدگی" />
          </button>
        </template>
      </nav>
      <nav v-else aria-label="منوی اصلی پنل">
        <div class="flex flex-col gap-2">
          <div v-for="menuGroup in menuGroups" :key="menuGroup.id" :data-menu-group="menuGroup.id">
            <!--
              §8.3 names this element first, and it carried four dead-or-harmful tokens at once on the
              panel's most persistent surface: `text-[11px]`, under the 12px floor; `uppercase`, which
              is INERT on Persian; `tracking-wide`, which inserts gaps inside the joins of a cursive
              script and visibly breaks «محتوای آموزشی» on every screen, all day; and `leading-[20px]`,
              which the type token already owns (§2.1, R4). All four are gone.

              §8.3 also writes `text-gray-500` into its replacement string, and THAT HALF IS NOT TAKEN.
              Measured rather than assumed: swapping the token takes contrastSweep's ink-on-tint count
              118 → 119 and turns the suite red, because `main.css` repaints `text-gray-400` for AA in
              both themes and leaves `text-gray-500` alone in dark — so the token that reads brighter
              is the darker one on a dark sidebar. §11 outranks §8.3 here: a measured colour does not
              move without the sweep, and the sweep says keep this one.

              `mb-2` → `mb-1` (§14.5 N2): seven headings × 4px is 28px of column back, and the heading
              already separates itself from the rows below it by weight and colour.
            -->
            <h2
              v-if="menuGroup.title"
              :class="[
                'mb-1 flex text-xs font-medium text-gray-400',
                !isExpanded && !isHovered ? 'lg:justify-center' : 'justify-start',
              ]"
            >
              <button v-if="isExpanded || isHovered || isMobileOpen" type="button" class="flex min-h-8 w-full items-center justify-between gap-2 rounded-lg px-2 text-start text-gray-500 transition hover:bg-gray-50 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200" :aria-expanded="isGroupOpen(menuGroup)" :aria-controls="'menu-group-' + menuGroup.id" @click="toggleGroup(menuGroup)">
                <span>{{ menuGroup.title }}</span>
                <ChevronDown class="h-3.5 w-3.5 shrink-0 transition-transform" :class="{ 'rotate-180': isGroupOpen(menuGroup) }" aria-hidden="true" />
              </button>
              <MoreHorizontal v-else class="h-5 w-5" />
            </h2>

            <ul :id="'menu-group-' + menuGroup.id" v-show="isGroupOpen(menuGroup) || (!isExpanded && !isHovered && !isMobileOpen)" class="flex flex-col gap-0.5">
              <li v-for="item in menuGroup.items" :key="item.id">
                <button
                  v-if="item.subItems"
                  :class="[
                    'menu-item group w-full',
                    // ── «open» and «current» are two different facts (§14.5 N3) ──────────────────
                    // This button used to take `menu-item-active` from `isSubmenuOpen()`, which is true
                    // both when the operator merely opened the accordion and when it holds the page
                    // they are on. One visual token, two meanings, on the surface whose entire job is
                    // to answer «where am I». The active weight now comes only from the canonical path;
                    // merely-open is said by the chevron and by the list being visible, which is what
                    // «open» actually looks like.
                    hasCanonicalActiveSubItem(item) ? 'menu-item-active !bg-brand-50/50 dark:!bg-brand-950/30' : 'menu-item-inactive',
                    !isExpanded && !isHovered ? 'lg:justify-center' : 'lg:justify-start',
                  ]"
                  :aria-expanded="isSubmenuOpen(item)"
                  @click="toggleSubmenu(item)"
                >
                  <span :class="hasCanonicalActiveSubItem(item) ? 'menu-item-icon-active' : 'menu-item-icon-inactive'">
                    <component :is="item.icon" class="h-5 w-5" />
                  </span>
                  <!-- `menu-item-text` is declared in no stylesheet in `src/` — it emitted no rule
                       and styled nothing (R1). The row's typography comes from `.menu-item` in
                       `main.css:288`, which is where it belongs. -->
                  <span v-if="isExpanded || isHovered || isMobileOpen">
                    {{ item.name }}
                  </span>
                  <ChevronDown
                    v-if="isExpanded || isHovered || isMobileOpen"
                    :class="[
                      'ms-auto h-5 w-5 transition-transform duration-200',
                      { 'rotate-180 text-brand-500': isSubmenuOpen(item) },
                    ]"
                  />
                </button>

                <!-- The daily shortcut and its canonical row share a destination, but only the one
                     used for navigation is current. The other stays a plain link, and its group
                     does not open automatically after a shortcut click. -->
                <router-link
                  v-else-if="item.path"
                  :to="item.path"
                  :class="[
                    'menu-item group',
                    isMenuCurrent(menuGroup, item.path) ? 'menu-item-active' : 'menu-item-inactive',
                  ]"
                  :aria-current="isMenuCurrent(menuGroup, item.path) ? 'page' : undefined"
                  @click="selectMenuPath(menuGroup, item.path)"
                >
                  <span :class="isMenuCurrent(menuGroup, item.path) ? 'menu-item-icon-active' : 'menu-item-icon-inactive'">
                    <component :is="item.icon" class="h-5 w-5" />
                  </span>
                  <!-- §8.5: `truncate` always carries a `:title`. A clipped nav label is unreachable by
                       any means, and this one is the destination's own name — and since §14.5 N6 the
                       row renders `meta.navLabel ?? meta.title`, so the `title` attribute, the browser
                       tab and the page's own <h1> are now one string. -->
                  <span
                    v-if="isExpanded || isHovered || isMobileOpen"
                    class="min-w-0 flex-1 truncate"
                    :title="item.name"
                  >{{ item.name }}</span>
                  <!-- The badge itself is preserved by name (§12: «a number, in the nav, that names
                       its own noun — the best affordance in the panel»); only its tokens move.
                       `mr-auto` -> `ms-auto` is zero pixels in a permanently-RTL panel and one less
                       physical class (§8.1); `text-[10px]` was below the 12px floor; `font-bold` is
                       reserved for the metric slot and a badge is chrome, so 500 (§2.3);
                       `px-1.5 py-0.5` -> §3.4's badge padding; `shadow-sm` deleted — §3.4 allows one
                       shadow in the panel, `shadow-lg`, and only on a floating surface.

                       `bg-orange-700` is NOT changed. It is off §4.1's five families and by meaning
                       it is `warning` («needs a person, nothing is broken yet»), which is exactly
                       what a queue count is — but §11 forbids moving a measured colour without
                       re-running `contrastSweep` against this ground, and white-on-orange-700 is the
                       one pairing in this file that has never been measured. Raised as a finding. -->
                  <span
                    v-if="badgeCount(item.badge) > 0"
                    class="ms-auto flex min-w-5 shrink-0 items-center justify-center rounded-full bg-orange-700 px-2 py-0.5 text-xs font-medium text-white"
                    :title="badgeTitle(item)"
                    :aria-label="badgeTitle(item)"
                    data-testid="sidebar-badge"
                  >
                    {{ badgeCount(item.badge) > 99 ? '۹۹+' : toFa(badgeCount(item.badge)) }}
                  </span>
                </router-link>

                <!-- ── the height animation is DELETED, and it never ran ────────────────────────
                     Four JS hooks wrapped this list — `@enter`/`@after-enter`/`@before-leave`/
                     `@after-leave` writing `el.style.height` — around a div carrying NO CSS transition
                     for any of it to animate. Measured in a browser rather than read: opening was
                     instant (three style writes in one frame), and closing left the accordion on screen
                     at its full height with an inline `height: 94px` and `display: block` while its own
                     button reported `aria-expanded="false"` — the sub-links still focusable, still
                     announced, in an accordion the operator had just closed.
                     `v-show` alone is correct: `display: none` when closed, instantly, with nothing to
                     get stuck. What is lost is a slide that has never played on this build. -->
                <div v-show="isSubmenuOpen(item) && (isExpanded || isHovered || isMobileOpen)">
                    <!-- `mr-7` -> `ms-6`: 28px is not on §3.1's scale (the table sends 7 to 6) and
                         `mr` is the physical spelling of what RTL calls the start edge (§8.1). The
                         sub-list now indents on the same 24px rhythm as everything else. -->
                    <ul class="ms-6 mt-0.5 space-y-0.5">
                      <li v-for="subItem in item.subItems" :key="subItem.path">
                        <!--
                          A sub-item may carry a count, and until this pass it could not. Every badge in
                          this menu hung on an ACCORDION HEADER, so the only rows that could say «there is
                          work here» were the ones that are not themselves a destination. The review queue
                          — 1,093 items waiting — is a sub-item, and it was the plainest row in the menu.
                        -->
                        <router-link
                          :to="subItem.path"
                          :class="[
                            'menu-dropdown-item',
                            isCanonicalCurrent(subItem.path) ? 'menu-dropdown-item-active' : 'menu-dropdown-item-inactive',
                          ]"
                          :aria-current="isCanonicalCurrent(subItem.path) ? 'page' : undefined"
                          @click="activeShortcutPath = ''"
                        >
                          <span class="min-w-0 flex-1 truncate" :title="subItem.name">{{ subItem.name }}</span>
                          <span
                            v-if="badgeCount(subItem.badge) > 0"
                            class="flex min-w-5 shrink-0 items-center justify-center rounded-full bg-orange-700 px-2 py-0.5 text-xs font-medium text-white"
                            :title="badgeTitle(subItem)"
                            :aria-label="badgeTitle(subItem)"
                            data-testid="sidebar-badge"
                          >
                            {{ badgeCount(subItem.badge) > 99 ? '۹۹+' : toFa(badgeCount(subItem.badge)) }}
                          </span>
                        </router-link>
                      </li>
                    </ul>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </nav>
    </div>
    <template v-if="compact && compactGroup">
      <button type="button" class="fixed inset-0 z-40 cursor-default" aria-label="بستن زیرمنو" @click="compactGroup = null" />
      <section id="compact-menu-panel" ref="compactPanel" tabindex="-1" :aria-label="compactGroup.title" class="fixed right-[90px] z-50 w-64 max-w-[calc(100vw-100px)] overflow-y-auto rounded-xl border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-700 dark:bg-gray-900" :style="{ top: `${compactTop}px`, maxHeight: 'calc(100vh - 24px)' }" @keydown.esc.stop="closeCompactGroup" >
        <div class="mb-1 flex items-center justify-between border-b border-gray-100 px-2 py-2 dark:border-gray-800"><strong class="text-sm">{{ compactGroup.title }}</strong><button type="button" aria-label="بستن زیرمنو" class="rounded p-1 hover:bg-gray-100 dark:hover:bg-gray-800" @click="closeCompactGroup"><X class="h-4 w-4" /></button></div>
        <template v-for="item in compactGroup.items" :key="item.id">
          <p v-if="item.subItems" class="px-2 pb-1 pt-2 text-xs text-gray-500">{{ item.name }}</p>
          <router-link v-for="entry in item.subItems || [item]" :key="entry.id || entry.path" :to="entry.path" :aria-current="isMenuCurrent(compactGroup, entry.path) ? 'page' : undefined" class="flex items-center gap-2 rounded-lg px-2 py-2 text-sm" :class="isMenuCurrent(compactGroup, entry.path) ? 'bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-200' : 'hover:bg-gray-50 dark:hover:bg-gray-800'" @click="selectMenuPath(compactGroup, entry.path); compactGroup = null"><span class="flex-1">{{ entry.name }}</span><span v-if="badgeCount(entry.badge) > 0" class="rounded-full bg-orange-700 px-2 text-xs text-white" :title="badgeTitle(entry)">{{ badgeCount(entry.badge) > 99 ? '۹۹+' : toFa(badgeCount(entry.badge)) }}</span></router-link>
        </template>
      </section>
    </template>
  </aside>
</template>

<script setup>
import { computed, nextTick, onMounted, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { ChevronDown, MoreHorizontal, X, ListChecks, BookOpen, GraduationCap, ChartNoAxesCombined, Megaphone, Settings } from 'lucide-vue-next'
import { useSidebar } from '@/composables/useSidebar'
import { usePermission } from '@/composables/usePermission'
import { useSupportAttention } from '@/composables/useSupportAttention'
import { useReviewBacklog } from '@/composables/useReviewBacklog'
import { buildMenu } from './navigation'
import { navLabelOf, landingPath } from './routeNames'
import { sidebarStateKey, readSidebarState, writeSidebarState } from '@/utils/sidebarState'

const route = useRoute()
const { isExpanded, isMobileOpen, isHovered, openSubmenu } = useSidebar()
const compact = computed(() => !isExpanded.value && !isMobileOpen.value)
const compactGroup = ref(null), compactTop = ref(12), compactPanel = ref(null)
let compactTrigger = null
const compactIcons = { today: ListChecks, content: BookOpen, learners: GraduationCap, behaviour: ChartNoAxesCombined, engagement: Megaphone, system: Settings }
async function openCompactGroup(group, event) {
  if (compactGroup.value?.id === group.id) return closeCompactGroup()
  compactTrigger = event.currentTarget
  compactTop.value = 12
  compactGroup.value = group
  await nextTick()
  const height = compactPanel.value?.getBoundingClientRect().height || 0
  compactTop.value = Math.max(12, Math.min(compactTrigger.getBoundingClientRect().top, window.innerHeight - height - 12))
  compactPanel.value?.focus()
}
function closeCompactGroup() { compactGroup.value = null; compactTrigger?.focus() }
watch(compact, () => { compactGroup.value = null; isHovered.value = false })
watch(() => route.fullPath, () => { compactGroup.value = null })
const { can } = usePermission()
const { waitingForUs } = useSupportAttention()
const { pending: reviewPending, refresh: refreshReviewBacklog } = useReviewBacklog()
const manuallyClosed = ref(new Set())
const groupChoices = ref({})
const activeShortcutPath = ref('')
const scrollElement = ref(null)
const stateKey = sidebarStateKey()
let stateReady = false, saveTimer = null, disposed = false
const isGroupOpen = group => groupChoices.value[group.id] ?? (
  ['boards', 'today', 'content'].includes(group.id) || group.items.some(item => item.subItems ? hasCanonicalActiveSubItem(item) : isCanonicalCurrent(item.path))
)
const toggleGroup = group => { groupChoices.value = { ...groupChoices.value, [group.id]: !isGroupOpen(group) } }
const toFa = (value) => Number(value ?? 0).toLocaleString('fa-IR')

// The queue count is only meaningful to somebody who can work the queue, and asking for it without the
// grant would be a guaranteed 403 on every page load for every other role.
onMounted(() => { if (can('questions.review')) refreshReviewBacklog() })

/**
 * A badge is `{ value, label }` where `value` is a ref or a number.
 *
 * `null` is not `0`, and the distinction is the whole reason this is a function rather than an inline
 * `item.badge.value > 0`. `useReviewBacklog` leaves its count null when the request FAILED, so a badge
 * printed from a null would tell a reviewer their queue is clear because the network was down — the
 * panel making its own breakage look like health, which is the one thing it must never do. A null
 * renders no badge at all: an absent number, honestly absent.
 */
const badgeCount = (badge) => {
  const raw = badge?.value
  const n = Number(raw?.value ?? raw)

  return Number.isFinite(n) ? n : 0
}

/**
 * What the badge MEANS, in words, for the screen reader and the tooltip alike.
 *
 * Both attributes, not just `title`: a `title` is an accessible name only by fallback and is skipped
 * outright by several screen readers, so a bare number would be announced as «۱۰۹۳» with no noun. Each
 * badge names its own subject — «منتظر پاسخ» and «منتظر بازبینی» are different work.
 */
const badgeTitle = (item) => `${toFa(badgeCount(item?.badge))} ${item?.badge?.label ?? 'مورد'}`

// ── why the sidebar is shaped this way ─────────────────────────────────────────────────────────────
//
// THE TREE IS NOT IN THIS FILE ANY MORE. It is `./navigation.js`, because three surfaces have to agree
// about where a page lives — this menu, the header's location line, and the page finder in the header —
// and a tree typed out three times is three trees. Read that file's header before changing the shape;
// it carries the argument for every group, the four-verb exemption, and the rule that a demotion is a
// change of RANK and never of REACHABILITY.
//
// THE NAMES ARE NOT IN THIS FILE EITHER. `navLabelOf(path)` returns `meta.navLabel ?? meta.title` from
// the route table (§14.5 N6). This menu used to type its own name for every row, and 22 pages therefore
// answered to two names — the browser tab said one thing and the row the operator clicked said another.
//
// WHAT STAYS TRUE: every route in the router must be reachable from here for an operator with the
// grants. `AppSidebar.spec.js` asserts it, because a route with no sidebar entry is a page that
// silently vanishes — and `navigation.gates.spec.js` asserts that the 2026-09-04 regroup moved no row
// between permission sets, against the seven real backend roles.
const menuGroups = computed(() => buildMenu({
  can,
  labelOf: navLabelOf,
  badgeValue: (source) => (source === 'reviewPending' ? reviewPending : waitingForUs),
  landingPath: landingPath(),
}))

/**
 * ── the active row, and the segment boundary it was missing (§14.5 N4) ────────────────────────────
 *
 * This was `route.path.startsWith(path)` with no boundary, so a path that is a string prefix of another
 * lit BOTH rows at once. Measured across all 117 paths, two pairs did it:
 *
 *   on `/learners-ops`       → «دانش‌آموزان» and «عملیات گروهی و آمار» were both marked current
 *   on `/learners/guardians` → «دانش‌آموزان» and «اولیا و گزارش» were both marked current
 *
 * Two rows claiming to be the page you are on, on the surface whose job is to answer «where am I».
 * Requiring the next character to be `/` keeps the parent-lights-up behaviour that detail pages depend
 * on (`/question-bank/questions/AB12` still lights the bank) and ends the two false positives. `/` is
 * exact, or it would light on every page in the panel.
 */
const isActive = (path) => (path === '/'
  ? route.path === '/'
  : route.path === path || route.path.startsWith(`${path}/`))

const hasActiveSubItem = (item) => Boolean(item.subItems?.some((subItem) => isActive(subItem.path)))
const isShortcutCurrent = path => activeShortcutPath.value === path && isActive(path)
const isCanonicalCurrent = path => {
  if (activeShortcutPath.value && isActive(activeShortcutPath.value)) return false
  return isActive(path) || menuGroups.value.some(group => group.items.some(item => item.subItems?.some(sub => sub.path === path && sub.relatedPaths?.some(isActive))))
}
const isMenuCurrent = (group, path) => group.pinned ? isShortcutCurrent(path) : isCanonicalCurrent(path)
const hasCanonicalActiveSubItem = item => Boolean(item.subItems?.some(subItem => isCanonicalCurrent(subItem.path)))
const selectMenuPath = (group, path) => { activeShortcutPath.value = group.pinned ? path : '' }

/**
 * ── more than one accordion may be open (§14.5 N3) ────────────────────────────────────────────────
 *
 * `openSubmenu` held ONE key, so opening B closed A. «داوری و ترمیم» and «سنجش سلامت بانک» are argued
 * in `navigation.js` as two halves of one job — the queue you work and the reports that say whether the
 * bank is healthy — and the single-key model made them mutually exclusive: moving between them cost two
 * clicks and destroyed the list you had just been reading.
 *
 * It is a Set now, keyed by the accordion's stable `id` rather than by its position, because positions
 * shift with permissions and an index-keyed open state reopens a different accordion for a different
 * operator. Session-only, deliberately: no persistence.
 */
// Converted once, here, rather than lazily inside a computed: `useSidebar`'s provider still hands out
// the `ref(null)` it always did, and a computed that writes to its own dependency is a side effect
// (eslint `vue/no-side-effects-in-computed-properties`) as well as a re-render loop waiting to happen.
if (!(openSubmenu.value instanceof Set)) openSubmenu.value = new Set()

const openIds = computed(() => (openSubmenu.value instanceof Set ? openSubmenu.value : new Set()))

const isSubmenuOpen = (item) => openIds.value.has(item.id)
  || (hasCanonicalActiveSubItem(item) && !manuallyClosed.value.has(item.id))

const toggleSubmenu = (item) => {
  const open = new Set(openIds.value)
  const closed = new Set(manuallyClosed.value)

  if (isSubmenuOpen(item)) {
    open.delete(item.id)
    // An accordion holding the current page reopens itself, so closing it has to be remembered until
    // the operator navigates — otherwise the click does nothing and the disclosure triangle looks dead.
    if (hasActiveSubItem(item)) closed.add(item.id)
  } else {
    open.add(item.id)
    closed.delete(item.id)
  }

  openSubmenu.value = open
  manuallyClosed.value = closed
}

function revealActiveGroup() {
  manuallyClosed.value = new Set()
}
function saveState() {
  if (!stateReady) return
  writeSidebarState(stateKey, { groups: groupChoices.value, manualGroups: groupChoices.value, open: [...openIds.value], closed: [...manuallyClosed.value], path: route.path, scroll: scrollElement.value?.scrollTop || 0 })
}
function scheduleSave() {
  clearTimeout(saveTimer)
  saveTimer = setTimeout(saveState, 150)
}
function revealActiveRow() {
  const container = scrollElement.value
  const active = container?.querySelector('[aria-current="page"]')
  if (!container || !active || !container.clientHeight || !active.getClientRects().length) return
  const frame = container.getBoundingClientRect(), row = active.getBoundingClientRect()
  if (row.top < frame.top + 8) container.scrollTop -= frame.top + 8 - row.top
  else if (row.bottom > frame.bottom - 8) container.scrollTop += row.bottom - frame.bottom + 8
}
onMounted(async () => {
  const saved = readSidebarState(stateKey)
  if (saved) {
    const groupIds = new Set(menuGroups.value.map(group => group.id))
    const itemIds = new Set(menuGroups.value.flatMap(group => group.items.filter(item => item.subItems).map(item => item.id)))
    groupChoices.value = Object.fromEntries(Object.entries(saved.manualGroups || {}).filter(([id, value]) => groupIds.has(id) && typeof value === 'boolean'))
    openSubmenu.value = new Set((Array.isArray(saved.open) ? saved.open : []).filter(id => itemIds.has(id)))
    manuallyClosed.value = new Set((Array.isArray(saved.closed) ? saved.closed : []).filter(id => itemIds.has(id)))
  }
  revealActiveGroup()
  await nextTick()
  if (disposed) return
  if (scrollElement.value && Number.isFinite(saved?.scroll)) scrollElement.value.scrollTop = Math.max(0, saved.scroll)
  if (!saved || saved.path !== route.path) revealActiveRow()
  stateReady = true
  window.addEventListener('pagehide', saveState)
})
watch([groupChoices, openSubmenu, manuallyClosed], scheduleSave, { deep: true })
watch(() => route.path, async () => {
  if (activeShortcutPath.value && route.path !== activeShortcutPath.value) activeShortcutPath.value = ''
  revealActiveGroup()
  await nextTick()
  if (disposed) return
  revealActiveRow()
  saveState()
})
onBeforeUnmount(() => {
  disposed = true
  clearTimeout(saveTimer)
  saveState()
  window.removeEventListener('pagehide', saveState)
})

</script>

<style scoped>
.sidebar-scroll {
  scrollbar-width: thin;
  scrollbar-gutter: stable;
  scrollbar-color: transparent transparent;
}
.sidebar-scroll:hover,
.sidebar-scroll:focus-within {
  scrollbar-color: #94a3b8 transparent;
}
.sidebar-scroll::-webkit-scrollbar { width: 5px; }
.sidebar-scroll::-webkit-scrollbar-track { background: transparent; }
.sidebar-scroll::-webkit-scrollbar-thumb {
  background-color: transparent;
  border-radius: 999px;
}
.sidebar-scroll:hover::-webkit-scrollbar-thumb,
.sidebar-scroll:focus-within::-webkit-scrollbar-thumb { background-color: #94a3b8; }
@media (hover: none) {
  .sidebar-scroll { scrollbar-color: #94a3b8 transparent; }
  .sidebar-scroll::-webkit-scrollbar-thumb { background-color: #94a3b8; }
}
@media (forced-colors: active) {
  .sidebar-scroll { scrollbar-color: auto; }
}
</style>
